import { RefObject, useEffect } from "react";

interface UseLeadsPagingSentinelArgs {
	/** The loaded leads. Its identity changing re-checks the sentinel (a page just arrived). */
	items: readonly unknown[];
	/** The end-of-list spinner; absent while a request is in flight or on the last page. */
	sentinelRef: RefObject<HTMLElement>;
	/** The page's loader. It guards its own idempotence, so calling it more than once is safe. */
	onLoadMore: () => void;
}

const SCROLL_DEBOUNCE_MS = 500;

/**
 * Infinite scroll for the Messages lead columns: calls `onLoadMore` whenever the end sentinel is
 * measured inside the viewport — once per effect run and on debounced scroll. There is deliberately
 * no latch remembering a previous call: the loader refuses a request while one is in flight or
 * already at the last page, so a column that never overflows still loads every page (PREC-5105).
 */
export function useLeadsPagingSentinel({
	items,
	sentinelRef,
	onLoadMore,
}: UseLeadsPagingSentinelArgs): void {
	useEffect(() => {
		if (items.length === 0) {
			return;
		}

		const tableContainer = document.querySelector("#leads-container");
		let pendingTimer: ReturnType<typeof setTimeout> | null = null;

		const isLastElementVisible = () => {
			const rect = sentinelRef.current?.getBoundingClientRect();

			if (!rect) {
				return;
			}

			if (rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)) {
				onLoadMore();
			}
		};

		const handleScroll = () => {
			if (pendingTimer !== null) {
				clearTimeout(pendingTimer);
			}

			pendingTimer = setTimeout(isLastElementVisible, SCROLL_DEBOUNCE_MS);
		};

		if (tableContainer) {
			tableContainer.addEventListener("scroll", handleScroll);

			isLastElementVisible();
		}

		return () => {
			tableContainer?.removeEventListener("scroll", handleScroll);

			if (pendingTimer !== null) {
				clearTimeout(pendingTimer);
			}
		};
	}, [items, sentinelRef, onLoadMore]);
}
